import React, { useState, useContext, useMemo, useEffect } from "react";
import {
  Dropdown,
  Button,
  OverlayTrigger,
  Tooltip,
  Modal,
} from "react-bootstrap";
import NewTermsOfUse from "../../components/ModalData/NewTermsOfUse";
import {
  IconBellFilled,
  IconUserCheck,
  IconLogout,
  IconClock,
} from "@tabler/icons-react";
import UserContext from "../../../contexts/UserContext";
import { getPlanLabel } from "../../../utils/PlanUtils";
import logo from "../../../images/mercadoagentes_logo.png";
import logo_symbol from "../../../images/mercadoagentes_thumb.png";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Logout } from "../../../services/LoginServices";
import { WhitelabelLogo } from "../../components/Whitelabel";
import { FEATURE_IDS } from "../../../constants/FeatureIds";
import { FEATURE_SLUGS } from "../../../constants/FeatureSlugs";

import OrganizationService from "../../../screens/Organization/services/OrganizationServices";
import Swal from "sweetalert2";

const Header = ({ sidebarExpanded, setSidebarExpanded }) => {
  const {
    bottyData,
    user,
    hasPermission,
    hasPermissionBySlug,
    getUserPlan,
    getUserPlanCategory,
    permissionsLoading,
    userFeatures,
    isSupportSession,
    isSubaccount,
    isPersonalAccount,
    isAgentsV2Only,
  } = useContext(UserContext);
  const hideAgentsV1 = isAgentsV2Only();
  const navigate = useNavigate();
  const location = useLocation();

  // Buscar plan do token JWT (substituindo account_type)
  const [currentPlanCategory, setCurrentPlanCategory] = useState(
    getUserPlanCategory() || "client",
  );
  const planCategory = currentPlanCategory;

  // Estados para sessão de acesso ao cliente
  const [activeClientSession, setActiveClientSession] = useState(null);
  const [returningToOriginal, setReturningToOriginal] = useState(false);

  // Estado para modal de termos de uso
  const [showTermsModal, setShowTermsModal] = useState(false);

  const superuser = {
    1: "Admin",
    2: "Manager",
    3: "Staff",
  };

  // Verificar sessão ativa de cliente ao carregar
  useEffect(() => {
    const checkActiveSession = () => {
      const session = OrganizationService.getCurrentSessionInfo();
      setActiveClientSession(session);

      // Gerenciar classe do body
      if (session) {
        document.body.classList.add("has-client-session");
      } else {
        document.body.classList.remove("has-client-session");
      }
    };

    checkActiveSession();

    // Verificar a cada minuto para atualizar o tempo restante
    const interval = setInterval(checkActiveSession, 60000);

    return () => {
      clearInterval(interval);
      // Limpar classe do body ao desmontar
      document.body.classList.remove("has-client-session");
    };
  }, []);

  // Handler para retornar à conta original
  const handleReturnToOriginalAccount = async () => {
    if (!activeClientSession) return;

    const result = await Swal.fire({
      title: "Encerrar Sessão de Suporte",
      html: `
        <p>Deseja retornar à sua conta original?</p>
        <p><small class="text-muted">
          Você estava acessando: <strong>${activeClientSession.clientAccount?.company_name}</strong><br>
          Motivo: ${activeClientSession.reason}
        </small></p>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim, retornar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#3b82f6",
    });

    if (!result.isConfirmed) return;

    try {
      setReturningToOriginal(true);

      await OrganizationService.returnToOriginalAccount(
        activeClientSession.sessionId,
      );

      // Limpar sessão local
      OrganizationService.clearActiveSession();
      setActiveClientSession(null);

      Swal.fire({
        icon: "success",
        title: "Sessão Encerrada",
        text: "Você retornou à sua conta original.",
        confirmButtonText: "OK",
        timer: 2000,
      }).then(() => {
        // Recarregar a página para aplicar o contexto original
        window.location.href = "/";
      });
    } catch (error) {
      console.error("Erro ao retornar à conta original:", error);

      let errorMessage = "Erro ao encerrar sessão.";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      Swal.fire({
        icon: "error",
        title: "Erro",
        text: errorMessage,
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setReturningToOriginal(false);
    }
  };

  // Função para toggle do sidebar
  const toggleSidebar = () => {
    setSidebarExpanded(!sidebarExpanded);
  };

  const handleClick = (path, external = false) => {
    if (external) {
      window.open(path, "_blank");
    } else {
      navigate(path);
    }
  };

  const currentPath = location.pathname;

  // Menu estruturado por categorias para Conta Empresa
  const clientMenuItems = useMemo(
    () => [
      {
        category: "ATENDIMENTO",
        items: [
          {
            path: "/service-center",
            icon: "las la-comments",
            label: "Central de Atendimento",
            featureId: FEATURE_IDS.CHAT,
            condition: bottyData?.uid,
          },
        ],
      },
      {
        category: "AGENTES",
        items: [
          {
            path: "/agents",
            icon: "las la-brain",
            label: "Meus Agentes",
            featureId: FEATURE_IDS.MEUS_AGENTES,
            condition: !hideAgentsV1,
          },
          {
            path: "/agents/create",
            icon: "las la-plus-circle",
            label: "Criar Agente 1.0",
            featureId: FEATURE_IDS.CRIAR_AGENTE,
            condition: isSupportSession && !hideAgentsV1,
          },
          {
            path: "/agents-flow",
            icon: "las la-rocket",
            label: "Agentes 2.0",
            badge: "NOVO",
            featureId: FEATURE_IDS.CRIAR_AGENTE,
            condition: !hideAgentsV1,
          },
          {
            path: "/agents-flow",
            icon: "las la-robot",
            label: "Meus Agentes",
            featureId: FEATURE_IDS.MEUS_AGENTES,
            condition: hideAgentsV1,
          },
          {
            path: "/agents-flow/canvas",
            icon: "las la-plus-circle",
            label: "Criar Agente",
            featureId: FEATURE_IDS.CRIAR_AGENTE,
            condition: hideAgentsV1,
          },
        ],
      },
      {
        category: "CANAIS",
        items: [
          {
            path: "/channels",
            icon: "las la-plug",
            label: "Canais",
            featureId: FEATURE_IDS.CANAIS,
          },
          {
            path: "/integrations",
            icon: "las la-network-wired",
            label: "Integrações",
            featureId: FEATURE_IDS.INTEGRACOES,
            condition: !hideAgentsV1,
          },
        ],
      },

      /*  {
        category: "APRENDIZADO",
        items: [
          {
            path: "/courses",
            icon: "las la-graduation-cap",
            label: "Vídeos Tutoriais",
            condition: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11].includes(getUserPlan()),
          },
        ],
      }, */
      {
        category: "GESTÃO COMERCIAL",
        items: [
          {
            path: "/calendar",
            icon: "las la-calendar-alt",
            label: "Agenda",
            featureId: FEATURE_IDS.AGENDA,
          },
          {
            path: "/customers",
            icon: "las la-users",
            label: "Base de Clientes",
            featureId: FEATURE_IDS.BASE_CLIENTES,
          },
          {
            path: "/crm",
            icon: "las la-chart-line",
            label: "Pipeline CRM",
            featureId: FEATURE_IDS.CRM_PIPELINE,
          },
          {
            path: "/reports",
            icon: "las la-chart-pie",
            label: "Relatórios Avançados",
            featureSlug: FEATURE_SLUGS.RELATORIOS_AVANCADOS,
          },

          // {
          //   path: "/business-metrics",
          //   icon: "las la-chart-bar",
          //   label: "Métricas",
          //   featureId: FEATURE_IDS.METRICAS,
          // },
        ],
      },
      {
        category: "SUPORTE",
        items: [
          {
            path: "/support",
            icon: "las la-headset",
            label: "Suporte",
            featureId: FEATURE_IDS.SUPORTE,
          },
        ],
      },
      {
        category: "TREINAMENTOS",
        items: [
          {
            path: "/training",
            icon: "las la-graduation-cap",
            label: "Treinamentos",
            featureId: FEATURE_IDS.TREINAMENTOS,
            condition: getUserPlan() !== 4 && getUserPlan() !== 3,
          },
          /* {
            path: "/gamification",
            icon: "las la-gamepad",
            label: "Gamificação",
            condition: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11].includes(getUserPlan()),
          }, */
        ],
      },
      {
        category: "CONFIGURAÇÕES",
        items: [
          {
            path: "/business-settings",
            icon: "las la-building",
            label: "Minha Empresa",
            featureId: FEATURE_IDS.MINHA_EMPRESA,
            condition: !hideAgentsV1,
          },
          {
            path: "/users",
            icon: "las la-users",
            label: "Usuários",
            featureId: FEATURE_IDS.USUARIOS,
            condition: !hideAgentsV1,
          },
          // {
          //   path: "/tags",
          //   icon: "las la-tags",
          //   label: "Tags & Categorias",
          //   featureId: FEATURE_IDS.TAGS_CATEGORIAS,
          // },
          {
            path: "/settings",
            icon: "las la-cog",
            label: "Configurações Globais",
            featureId: FEATURE_IDS.CONFIGURACOES_GLOBAIS,
            condition: !hideAgentsV1 && (user?.superuser_level > 0 || isSupportSession),
            alwaysVisible: true,
          },
        ],
      },
    ],
    [bottyData?.uid, isSupportSession, user?.is_superuser, hideAgentsV1],
  );

  const scaleMenuItems = useMemo(
    () => [
      {
        category: "ATENDIMENTO",
        items: [
          {
            path: "/service-center",
            icon: "las la-comments",
            label: "Central de Atendimento",
            featureId: FEATURE_IDS.CHAT,
            condition: bottyData?.uid,
          },
        ],
      },
      {
        category: "AGENTES",
        items: [
          {
            path: "/agents",
            icon: "las la-robot",
            label: "Meus Agentes 1.0",
            featureId: FEATURE_IDS.MEUS_AGENTES,
            condition: !hideAgentsV1,
          },
          {
            path: "/agents/create",
            icon: "las la-plus-circle",
            label: "Criar Agente 1.0",
            featureId: FEATURE_IDS.CRIAR_AGENTE,
            condition: !hideAgentsV1,
          },
          {
            path: "/agents-flow",
            icon: "las la-rocket",
            label: "Agentes 2.0",
            badge: "NOVO",
            featureId: FEATURE_IDS.CRIAR_AGENTE,
            condition: !hideAgentsV1,
          },
          {
            path: "/agents-flow",
            icon: "las la-robot",
            label: "Meus Agentes",
            featureId: FEATURE_IDS.MEUS_AGENTES,
            condition: hideAgentsV1,
          },
          {
            path: "/agents-flow/canvas",
            icon: "las la-plus-circle",
            label: "Criar Agente",
            featureId: FEATURE_IDS.CRIAR_AGENTE,
            condition: hideAgentsV1,
          },
        ],
      },
      {
        category: "CANAIS",
        items: [
          {
            path: "/channels",
            icon: "las la-plug",
            label: "Canais",
            featureId: FEATURE_IDS.CANAIS,
            condition: getUserPlan() !== 6,
          },
          {
            path: "/integrations",
            icon: "las la-network-wired",
            label: "Integrações",
            featureId: FEATURE_IDS.INTEGRACOES,
            condition: !hideAgentsV1,
          },
        ],
      },

      {
        category: "APRENDIZADO",
        items: [
          {
            path: "/courses",
            icon: "las la-graduation-cap",
            label: "Vídeos Tutoriais",
            condition: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11].includes(getUserPlan()),
          },
        ],
      },
      {
        category: "GESTÃO COMERCIAL",
        items: [
          {
            path: "/calendar",
            icon: "las la-calendar-alt",
            label: "Agenda",
            featureId: FEATURE_IDS.AGENDA,
          },
          {
            path: "/customers",
            icon: "las la-users",
            label: "Base de Clientes",
            featureId: FEATURE_IDS.BASE_CLIENTES,
          },
          {
            path: "/crm",
            icon: "las la-chart-line",
            label: "Pipeline CRM",
            featureId: FEATURE_IDS.CRM_PIPELINE,
          },
          {
            path: "/reports",
            icon: "las la-chart-pie",
            label: "Relatórios Avançados",
            featureSlug: FEATURE_SLUGS.RELATORIOS_AVANCADOS,
          },
        ],
      },
      // {
      //   category: "MARKETPLACE",
      //   items: [
      //     {
      //       path: "/marketplace/seller-profile",
      //       icon: "las la-user-circle",
      //       label: "Perfil do Vendedor",
      //       featureId: FEATURE_IDS.PUBLICACOES,
      //       condition: [2, 5].includes(getUserPlan()),
      //     },
      //     {
      //       path: "/marketplace/leads",
      //       icon: "las la-users",
      //       label: "Leads",
      //       // condition: getUserPlan() > 1,
      //       condition: [2, 5].includes(getUserPlan()),
      //     },
      //     {
      //       path: "/marketplace",
      //       icon: "las la-store-alt",
      //       label: "Minhas Publicações",
      //       featureId: FEATURE_IDS.PUBLICACOES,
      //       condition: [2, 5].includes(getUserPlan()),
      //     },
      //     {
      //       path: "/marketplace/shop",
      //       icon: "las la-shopping-bag",
      //       label: "MercadoShop",
      //       condition: [2, 5].includes(getUserPlan()),
      //     },
      //   ],
      // },
      {
        category: "MINHA ORGANIZAÇÃO",
        items: [
          {
            path: "/organization",
            icon: "las la-building",
            label: "Organização",
            featureId: FEATURE_IDS.ORGANIZACAO,
          },
          {
            path: "/organization-clients",
            icon: "las la-users",
            label: "Subcontas",
            featureId: FEATURE_IDS.CLIENTES_ORGANIZACAO,
          },
          {
            path: "/agents-implementation",
            icon: "las la-bolt",
            label: "Implementações",
            featureId: FEATURE_IDS.IMPLEMENTACOES_AGENTES,
          },
          {
            path: "/users",
            icon: "las la-users",
            label: "Usuários",
            featureId: FEATURE_IDS.USUARIOS,
          },
          {
            path: "/metrics",
            icon: "las la-chart-line",
            label: "Métricas",
            featureId: FEATURE_IDS.METRICAS,
          },
        ],
      },
      {
        category: "FINANCEIRO",
        items: [
          {
            path: "/my-plan",
            icon: "las la-crown",
            label: "Meu Plano",
            featureId: FEATURE_IDS.FATURAMENTO,
            condition: user?.superuser_level === 1,
          },
          {
            path: "/billing",
            icon: "las la-credit-card",
            label: "Faturamento",
            featureId: FEATURE_IDS.FATURAMENTO,
            condition: user?.superuser_level === 1,
          },
          {
            path: "/subscriptions",
            icon: "las la-file-invoice-dollar",
            label: "Minhas Assinaturas",
            featureId: FEATURE_IDS.ASSINATURAS,
            condition: user?.superuser_level === 1,
          },
        ],
      },
      {
        category: "SUPORTE",
        items: [
          {
            path: "/support",
            icon: "las la-headset",
            label: "Suporte Clientes",
            featureId: FEATURE_IDS.SUPORTE,
          },
          {
            path: "https://wa.me/5547991360752?text=Ol%C3%A1%2C%20sou%20cliente%20e%20preciso%20de%20suporte",
            icon: "las la-life-ring",
            label: "Suporte Plataforma",
            external: true,
          },
        ],
      },
      {
        category: "TREINAMENTOS",
        items: [
          {
            path: "/training",
            icon: "las la-graduation-cap",
            label: "Meus Treinamentos",
            featureId: FEATURE_IDS.TREINAMENTOS,
            condition: getUserPlan() !== 4 && getUserPlan() !== 3,
          },
          {
            path: "/training/manage",
            icon: "las la-cog",
            label: "Gerenciar Treinamentos",
            featureId: FEATURE_IDS.GERENCIAR_TREINAMENTOS,
            condition: getUserPlan() !== 4 && getUserPlan() !== 3,
          },
          /* {
            path: "/gamification",
            icon: "las la-gamepad",
            label: "Gamificação",
            condition: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11].includes(getUserPlan()),
          }, */
        ],
      },
      {
        category: "CONFIGURAÇÕES",
        items: [
          {
            path: "/business-settings",
            icon: "las la-building",
            label: "Minha Empresa",
            featureId: FEATURE_IDS.MINHA_EMPRESA,
            condition: !hideAgentsV1,
          },
          {
            path: "/settings",
            icon: "las la-cog",
            label: "Configurações Globais",
            featureId: FEATURE_IDS.CONFIGURACOES_GLOBAIS,
            condition: !hideAgentsV1 && (user?.superuser_level > 0 || isSupportSession),
            alwaysVisible: true,
          },
        ],
      },
    ],
    [bottyData?.uid, isSupportSession, user?.is_superuser, hideAgentsV1],
  );

  // Menu estruturado para Plano PRO (funcionalidades específicas)
  const proMenuItems = useMemo(
    () => [
      {
        category: "ATENDIMENTO",
        items: [
          {
            path: "/service-center",
            icon: "las la-comments",
            label: "Central de Atendimento",
            featureId: FEATURE_IDS.CHAT,
            condition: bottyData?.uid,
          },
        ],
      },
      {
        category: "AGENTES",
        items: [
          {
            path: "/agents",
            icon: "las la-robot",
            label: "Meus Agentes",
            featureId: FEATURE_IDS.MEUS_AGENTES,
            condition: !hideAgentsV1,
          },
          {
            path: "/agents/create",
            icon: "las la-plus-circle",
            label: "Criar Agente 1.0",
            featureId: FEATURE_IDS.CRIAR_AGENTE,
            condition: !hideAgentsV1,
          },
          {
            path: "/agents-flow",
            icon: "las la-rocket",
            label: "Agentes 2.0",
            badge: "NOVO",
            featureId: FEATURE_IDS.CRIAR_AGENTE,
            condition: !hideAgentsV1,
          },
          {
            path: "/agents-flow",
            icon: "las la-robot",
            label: "Meus Agentes",
            featureId: FEATURE_IDS.MEUS_AGENTES,
            condition: hideAgentsV1,
          },
          {
            path: "/agents-flow/canvas",
            icon: "las la-plus-circle",
            label: "Criar Agente",
            featureId: FEATURE_IDS.CRIAR_AGENTE,
            condition: hideAgentsV1,
          },
        ],
      },
      {
        category: "CANAIS",
        items: [
          {
            path: "/channels",
            icon: "las la-plug",
            label: "Canais",
            featureId: FEATURE_IDS.CANAIS,
            condition: getUserPlan() !== 6,
          },
          {
            path: "/integrations",
            icon: "las la-network-wired",
            label: "Integrações",
            featureId: FEATURE_IDS.INTEGRACOES,
            condition: !hideAgentsV1,
          },
        ],
      },

      {
        category: "APRENDIZADO",
        items: [
          {
            path: "/courses",
            icon: "las la-graduation-cap",
            label: "Vídeos Tutoriais",
            condition: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11].includes(getUserPlan()),
          },
        ],
      },
      {
        category: "GESTÃO COMERCIAL",
        items: [
          {
            path: "/crm",
            icon: "las la-chart-line",
            label: "Pipeline CRM",
            featureId: FEATURE_IDS.CRM_PIPELINE,
          },
          {
            path: "/reports",
            icon: "las la-chart-pie",
            label: "Relatórios Avançados",
            featureSlug: FEATURE_SLUGS.RELATORIOS_AVANCADOS,
          },
          {
            path: "/calendar",
            icon: "las la-calendar-alt",
            label: "Agenda",
            featureId: FEATURE_IDS.AGENDA,
          },
        ],
      },
      /*  {
        category: "MARKETPLACE",
        items: [
          {
            path: "/marketplace",
            icon: "las la-store-alt",
            label: "Minhas Publicações",
            featureId: FEATURE_IDS.PUBLICACOES,
          },
          {
            path: "/publications",
            icon: "las la-store",
            label: "Publicações Admin",
            featureId: FEATURE_IDS.PUBLICACOES,
          },
          {
            path: "/marketplace/shop",
            icon: "las la-shopping-bag",
            label: "MercadoShop",
            condition: [2, 5, 9, 10, 11].includes(getUserPlan()),
          },
          {
            path: "/marketplace/leads",
            icon: "las la-users",
            label: "Leads",
            condition: getUserPlan() > 1,
          },
          {
            path: "/marketplace/seller-profile",
            icon: "las la-user-circle",
            label: "Perfil do Vendedor",
            featureId: FEATURE_IDS.PUBLICACOES,
          },
          /* {
          path: "/statistics",
          icon: "las la-chart-bar",
          label: "Estatísticas",
        }, 
        ],
      }, */
      {
        category: "MINHA ORGANIZAÇÃO",
        items: [
          {
            path: "/organization",
            icon: "las la-building",
            label: "Organização",
            featureId: FEATURE_IDS.ORGANIZACAO,
          },
          {
            path: "/organization-clients",
            icon: "las la-users",
            label: "Clientes",
            featureId: FEATURE_IDS.CLIENTES_ORGANIZACAO,
          },
          {
            path: "/agents-implementation",
            icon: "las la-bolt",
            label: "Implementações",
            featureId: FEATURE_IDS.IMPLEMENTACOES_AGENTES,
          },
          {
            path: "/users",
            icon: "las la-users",
            label: "Usuários",
            featureId: FEATURE_IDS.USUARIOS,
          },
          {
            path: "/metrics",
            icon: "las la-chart-line",
            label: "Métricas",
            featureId: FEATURE_IDS.METRICAS,
          },
        ],
      },
      {
        category: "SUPORTE",
        items: [
          {
            path: "/support",
            icon: "las la-headset",
            label: "Suporte Clientes",
            featureId: FEATURE_IDS.SUPORTE,
          },
          {
            path: "https://wa.me/5547991360752?text=Ol%C3%A1%2C%20sou%20cliente%20e%20preciso%20de%20suporte",
            icon: "las la-life-ring",
            label: "Suporte Plataforma",
            external: true,
          },
        ],
      },
      {
        category: "TREINAMENTOS",
        items: [
          {
            path: "/training",
            icon: "las la-graduation-cap",
            label: "Meus Treinamentos",
            featureId: FEATURE_IDS.TREINAMENTOS,
            condition: getUserPlan() !== 4 && getUserPlan() !== 3,
          },
          {
            path: "/training/manage",
            icon: "las la-cog",
            label: "Gerenciar Treinamentos",
            featureId: FEATURE_IDS.GERENCIAR_TREINAMENTOS,
            condition: getUserPlan() !== 4 && getUserPlan() !== 3,
          },
        ],
      },
      {
        category: "CONFIGURAÇÕES",
        items: [
          {
            path: "/business-settings",
            icon: "las la-building",
            label: "Minha Empresa",
            featureId: FEATURE_IDS.MINHA_EMPRESA,
            condition: !hideAgentsV1,
          },
          {
            path: "/settings",
            icon: "las la-cog",
            label: "Configurações Globais",
            featureId: FEATURE_IDS.CONFIGURACOES_GLOBAIS,
            condition: !hideAgentsV1 && (user?.superuser_level > 0 || isSupportSession),
            alwaysVisible: true,
          },
        ],
      },
    ],
    [bottyData?.uid, isSupportSession, user?.is_superuser, hideAgentsV1],
  );

  // Menu estruturado para Plano On Demand (funcionalidades básicas)
  const starterMenuItems = useMemo(
    () => [
      {
        category: "APRENDIZADO",
        items: [
          {
            path: "/courses",
            icon: "las la-graduation-cap",
            label: "Vídeos Tutoriais",
            condition: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11].includes(getUserPlan()),
          },
        ],
      },
      {
        category: "ATENDIMENTO",
        items: [
          {
            path: "/service-center",
            icon: "las la-comments",
            label: "Central de Atendimento",
            featureId: FEATURE_IDS.CHAT,
            condition: bottyData?.uid,
          },
        ],
      },
      {
        category: "AGENTES",
        items: [
          {
            path: "/agents",
            icon: "las la-robot",
            label: "Meus Agentes",
            featureId: FEATURE_IDS.MEUS_AGENTES,
            condition: !hideAgentsV1,
          },
          {
            path: "/agents/create",
            icon: "las la-plus-circle",
            label: "Criar Agente 1.0",
            featureId: FEATURE_IDS.CRIAR_AGENTE,
            condition: !hideAgentsV1,
          },
          {
            path: "/agents-flow",
            icon: "las la-rocket",
            label: "Agentes 2.0",
            badge: "NOVO",
            featureId: FEATURE_IDS.CRIAR_AGENTE,
            condition: !hideAgentsV1,
          },
          {
            path: "/agents-flow",
            icon: "las la-robot",
            label: "Meus Agentes",
            featureId: FEATURE_IDS.MEUS_AGENTES,
            condition: hideAgentsV1,
          },
          {
            path: "/agents-flow/canvas",
            icon: "las la-plus-circle",
            label: "Criar Agente",
            featureId: FEATURE_IDS.CRIAR_AGENTE,
            condition: hideAgentsV1,
          },
        ],
      },

      {
        category: "CANAIS",
        items: [
          {
            path: "/channels",
            icon: "las la-plug",
            label: "Canais",
            featureId: FEATURE_IDS.CANAIS,
            condition: getUserPlan() !== 6,
          },
          {
            path: "/integrations",
            icon: "las la-network-wired",
            label: "Integrações",
            featureId: FEATURE_IDS.INTEGRACOES,
            condition: !hideAgentsV1,
          },
        ],
      },

      {
        category: "GESTÃO COMERCIAL",
        items: [
          {
            path: "/calendar",
            icon: "las la-calendar-alt",
            label: "Agenda",
            featureId: FEATURE_IDS.AGENDA,
          },
          {
            path: "/customers",
            icon: "las la-users",
            label: "Base de Clientes",
            featureId: FEATURE_IDS.BASE_CLIENTES,
          },
          {
            path: "/crm",
            icon: "las la-chart-line",
            label: "Pipeline CRM",
            featureId: FEATURE_IDS.CRM_PIPELINE,
          },
          {
            path: "/reports",
            icon: "las la-chart-pie",
            label: "Relatórios Avançados",
            featureSlug: FEATURE_SLUGS.RELATORIOS_AVANCADOS,
          },
        ],
      },
      //  {
      //   category: "MARKETPLACE",
      //   items: [
      //     {
      //       path: "/marketplace",
      //       icon: "las la-store-alt",
      //       label: "Minhas Publicações",
      //       featureId: FEATURE_IDS.PUBLICACOES,
      //     },

      //     {
      //       path: "/marketplace/shop",
      //       icon: "las la-shopping-bag",
      //       label: "MercadoShop",
      //       condition: [2, 5, 9, 10, 11].includes(getUserPlan()),
      //     },
      //     {
      //       path: "/marketplace/leads",
      //       icon: "las la-users",
      //       label: "Leads",
      //       condition: getUserPlan() > 1,
      //     },
      //     {
      //       path: "/marketplace/seller-profile",
      //       icon: "las la-user-circle",
      //       label: "Perfil do Vendedor",
      //       featureId: FEATURE_IDS.PUBLICACOES,
      //     },

      //   ],
      // },
      {
        category: "MINHA ORGANIZAÇÃO",
        items: [
          {
            path: "/organization",
            icon: "las la-building",
            label: "Organização",
            featureId: FEATURE_IDS.ORGANIZACAO,
          },
          {
            path: "/organization-clients",
            icon: "las la-users",
            label: "Subcontas",
            featureId: FEATURE_IDS.CLIENTES_ORGANIZACAO,
          },
          {
            path: "/agents-implementation",
            icon: "las la-bolt",
            label: "Implementações",
            featureId: FEATURE_IDS.IMPLEMENTACOES_AGENTES,
          },
          {
            path: "/users",
            icon: "las la-users",
            label: "Usuários",
            featureId: FEATURE_IDS.USUARIOS,
          },
          {
            path: "/metrics",
            icon: "las la-chart-line",
            label: "Métricas",
            featureId: FEATURE_IDS.METRICAS,
          },
        ],
      },
      {
        category: "FINANCEIRO",
        items: [
          {
            path: "/my-plan",
            icon: "las la-crown",
            label: "Meu Plano",
            featureId: FEATURE_IDS.FATURAMENTO,
            condition: user?.superuser_level === 1,
          },
          {
            path: "/billing",
            icon: "las la-credit-card",
            label: "Faturamento",
            featureId: FEATURE_IDS.FATURAMENTO,
            condition: user?.superuser_level === 1,
          },
          {
            path: "/subscriptions",
            icon: "las la-file-invoice-dollar",
            label: "Minhas Assinaturas",
            featureId: FEATURE_IDS.ASSINATURAS,
            condition: user?.superuser_level === 1,
          },
        ],
      },
      {
        category: "SUPORTE",
        items: [
          {
            path: "https://wa.me/5547991360752?text=Ol%C3%A1%2C%20sou%20cliente%20e%20preciso%20de%20suporte",
            icon: "las la-life-ring",
            label: "Suporte Plataforma",
            external: true,
          },
        ],
      },

      {
        category: "CONFIGURAÇÕES",
        items: [
          {
            path: "/business-settings",
            icon: "las la-building",
            label: "Minha Empresa",
            featureId: FEATURE_IDS.MINHA_EMPRESA,
            condition: !hideAgentsV1,
          },
          {
            path: "/settings",
            icon: "las la-cog",
            label: "Configurações Globais",
            featureId: FEATURE_IDS.CONFIGURACOES_GLOBAIS,
            condition: !hideAgentsV1 && (user?.superuser_level > 0 || isSupportSession),
            alwaysVisible: true,
          },
        ],
      },
    ],
    [bottyData?.uid, isSupportSession, user?.is_superuser, hideAgentsV1],
  );

  // Menu para contas de uso pessoal (sem organização)
  const personalMenuItems = useMemo(
    () =>
      starterMenuItems
        .filter((section) => section.category !== "MINHA ORGANIZAÇÃO")
        .map((section) => {
          if (section.category === "APPS & INTEGRAÇÕES") {
            return {
              ...section,
            
            };
          }
          return section;
        }),
    [starterMenuItems]
  );

  // Handler para mudança de categoria de plano
  const handlePlanCategoryChange = (newCategory) => {
    setCurrentPlanCategory(newCategory);
  };

  // Memoizar o menu baseado na categoria do plano
  const currentMenuItems = useMemo(() => {
    // Contas pessoais usam menu sem organização independente do plano
    if (isPersonalAccount()) {
      return personalMenuItems;
    }

    switch (planCategory) {
      case "client":
        return clientMenuItems;
      case "founder":
      case "scale":
      case "white-label":
      case "wl-plus":
      case "wl-pro":
        return scaleMenuItems;
      case "pro":
        return proMenuItems;
      case "starter":
        return starterMenuItems;
      case "free":
      case "personal-starter":
      case "personal-business":
        return personalMenuItems;
      default:
        return clientMenuItems;
    }
  }, [
    planCategory,
    clientMenuItems,
    scaleMenuItems,
    proMenuItems,
    starterMenuItems,
    personalMenuItems,
    isPersonalAccount,
  ]);

  // Menu de administração para usuários is_staff
  const adminMenuItems = useMemo(
    () => [
      {
        category: "ADMINISTRAÇÃO GLOBAL",
        items: [
          {
            path: "/admin/users",
            icon: "las la-users-cog",
            label: "Usuários",
            featureId: FEATURE_IDS.ADMIN_USUARIOS,
            condition: user?.is_staff,
          },
          {
            path: "/admin/training/manage",
            icon: "las la-graduation-cap",
            label: "Gerenciar Treinamentos",
            featureId: FEATURE_IDS.ADMIN_TREINAMENTOS,
            condition: user?.is_staff && getUserPlan() !== 4,
          },
          {
            path: "/admin/courses",
            icon: "las la-graduation-cap",
            label: "Aprendizado",
            featureId: FEATURE_IDS.ADMIN_CURSOS,
            condition: user?.is_staff,
          },
          {
            path: "/admin/marketplace/banners",
            icon: "las la-image",
            label: "Banners Marketplace",
            condition: user?.is_staff && user?.superuser_level === 1,
          },
          {
            path: "/publications",
            icon: "las la-store",
            label: "Publicações",
            featureId: FEATURE_IDS.PUBLICACOES,
          },
        ],
      },
    ],
    [user?.is_staff, user?.superuser_level, getUserPlan],
  );

  const renderMenuItems = () => {
    // Se o usuário é staff, mostrar apenas o menu de administração
    if (user?.is_staff) {
      return (
        <>
          {/* Item Dashboard sempre no topo */}
          <div className="menu-section">
            <div
              className={`menu-item ${
                sidebarExpanded ? "expanded" : "collapsed"
              } ${currentPath === "/" ? "active" : ""}`}
              onClick={() => handleClick("/")}
            >
              <i className="las la-home" />
              <span>Dashboard</span>
            </div>
          </div>

          <div
            className={`menu-divider ${
              sidebarExpanded ? "expanded" : "collapsed"
            }`}
          />

          {/* Menu de Administração para is_staff */}
          {adminMenuItems
            .map((section) => {
              const visibleItems = section.items.filter((item) => {
                if (item.condition !== undefined && !item.condition) {
                  return false;
                }

                // Items marcados como alwaysVisible são exibidos para todos
                if (item.alwaysVisible) {
                  return true;
                }

                // ========== LÓGICA ESPECIAL PARA SUBACCOUNTS ==========
                if (isSubaccount()) {
                  if (permissionsLoading) {
                    return false;
                  }
                  if (isSupportSession || user?.superuser_level === 1) return true;
                  // Suporta featureSlug (preferido) e featureId (legacy)
                  if (item.featureSlug) {
                    return hasPermissionBySlug(item.featureSlug, "read");
                  }
                  if (!item.featureId) {
                    return false;
                  }
                  if (!userFeatures || !userFeatures[item.featureId]) {
                    return false;
                  }
                  return userFeatures[item.featureId].permissions?.read === true;
                }
                // ========== FIM LÓGICA SUBACCOUNTS ==========

                if (item.featureSlug) {
                  if (permissionsLoading) return false;
                  if (user?.superuser_level === 1) return true;
                  return hasPermissionBySlug(item.featureSlug, "read");
                }

                if (item.featureId) {
                  // Se permissões ainda estão carregando, não mostrar
                  if (permissionsLoading) {
                    return false;
                  }
                  // Se userFeatures está vazio/null, negar acesso (exceto para admin)
                  if (!userFeatures || Object.keys(userFeatures).length === 0) {
                    if (user?.superuser_level === 1) {
                      return true; // Admin sempre tem acesso
                    }
                    return false;
                  }
                  return hasPermission(item.featureId, "read");
                }
                return true;
              });

              if (visibleItems.length === 0) {
                return null;
              }

              return { ...section, items: visibleItems };
            })
            .filter(Boolean)
            .map((section, sectionIndex) => (
              <div className="menu-section" key={`admin-${sectionIndex}`}>
                <div
                  className={`category-title ${
                    sidebarExpanded ? "expanded" : "collapsed"
                  } admin-category`}
                >
                  {section.category}
                </div>
                {section.items.map((item, itemIndex) => {
                  const currentPlan = getUserPlan();
                  const isBlockedByPlan = false;
                  const isShopBlocked =
                    item.path === "/marketplace/shop" &&
                    ![2, 5, 9, 10, 11].includes(currentPlan);
                  const isItemBlocked = isBlockedByPlan || isShopBlocked;

                  const menuItem = (
                    <div
                      key={itemIndex}
                      className={`menu-item ${
                        sidebarExpanded ? "expanded" : "collapsed"
                      } ${
                        currentPath === item.path ? "active" : ""
                      } admin-menu-item ${isItemBlocked ? "plan-blocked" : ""}`}
                      onClick={() =>
                        isItemBlocked
                          ? null
                          : handleClick(item.path, item?.external)
                      }
                      title={
                        isItemBlocked ? "Faça um upgrade para liberar" : ""
                      }
                      style={{
                        opacity: isItemBlocked ? 0.5 : 1,
                        cursor: isItemBlocked ? "not-allowed" : "pointer",
                        color: isItemBlocked ? "#999" : "",
                      }}
                    >
                      <i className={item.icon} />
                      <span>{item.label}</span>
                      {item.badge && (
                        <span className={`menu-beta-badge menu-badge--${item.badge.toLowerCase()}`}>{item.badge}</span>
                      )}
                      {isItemBlocked && (
                        <i
                          className="las la-lock"
                          style={{ marginLeft: "auto", fontSize: "14px" }}
                        />
                      )}
                    </div>
                  );

                  if (isItemBlocked) {
                    return (
                      <OverlayTrigger
                        key={itemIndex}
                        placement="right"
                        overlay={
                          <Tooltip id={`tooltip-admin-${itemIndex}`}>
                            Faça um upgrade para liberar
                          </Tooltip>
                        }
                        trigger={["hover", "focus"]}
                      >
                        <span style={{ display: "block" }}>{menuItem}</span>
                      </OverlayTrigger>
                    );
                  }

                  return menuItem;
                })}
              </div>
            ))}
        </>
      );
    }

    // Menu padrão para usuários não-staff
    return (
      <>
        {/* Item Dashboard sempre no topo */}
        <div className="menu-section">
          <div
            className={`menu-item ${
              sidebarExpanded ? "expanded" : "collapsed"
            } ${currentPath === "/" ? "active" : ""}`}
            onClick={() => handleClick("/")}
          >
            <i className="las la-home" />
            <span>Dashboard</span>
          </div>
        </div>

        <div
          className={`menu-divider ${
            sidebarExpanded ? "expanded" : "collapsed"
          }`}
        />

        {currentMenuItems
          .map((section) => {
            // Filtrar itens que o usuário tem permissão
            const visibleItems = section.items.filter((item) => {
              // Verifica condição se existir
              if (item.condition !== undefined && !item.condition) {
                return false;
              }

              // Items marcados como alwaysVisible são exibidos para todos
              if (item.alwaysVisible) {
                return true;
              }

              // ========== LÓGICA ESPECIAL PARA SUBACCOUNTS ==========
              // Subaccounts só veem features explicitamente liberadas pela organização mãe.
              // Sessões de suporte e admins (level 1) têm acesso total.
              if (isSubaccount()) {
                if (permissionsLoading) return false;
                if (isSupportSession || user?.superuser_level === 1) return true;
                // Suporta featureSlug (preferido) e featureId (legacy)
                if (item.featureSlug) {
                  return hasPermissionBySlug(item.featureSlug, "read");
                }
                if (!item.featureId) {
                  return false;
                }
                if (!userFeatures || !userFeatures[item.featureId]) {
                  return false;
                }
                return userFeatures[item.featureId].permissions?.read === true;
              }
              // ========== FIM LÓGICA SUBACCOUNTS ==========

              // Verifica permissão por slug (recomendado) ou ID
              if (item.featureSlug) {
                if (permissionsLoading) return false;
                if (user?.superuser_level === 1) return true;
                return hasPermissionBySlug(item.featureSlug, "read");
              }

              if (item.featureId) {
                // Se permissões ainda estão carregando, não mostrar
                if (permissionsLoading) {
                  return false;
                }
                // Se userFeatures está vazio/null, negar acesso (exceto para admin)
                if (!userFeatures || Object.keys(userFeatures).length === 0) {
                  if (user?.superuser_level === 1) {
                    return true; // Admin sempre tem acesso
                  }
                  return false;
                }
                return hasPermission(item.featureId, "read");
              }

              // Para usuários level 0, itens sem featureId são NEGADOS (exceto sessão de suporte)
              if (user?.superuser_level === 0) {
                return isSupportSession || false;
              }

              return user?.is_superuser || isSupportSession || false;
            });

            // Se não há itens visíveis, não renderizar a categoria
            if (visibleItems.length === 0) {
              return null;
            }

            return { ...section, items: visibleItems };
          })
          .filter(Boolean) // Remove seções null/undefined
          .map((section, sectionIndex) => (
            <div
              className="menu-section"
              key={sectionIndex}
              data-menu-id={section.category?.toLowerCase()
                .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                .replace(/[^a-z0-9]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "")}
            >
              <div
                className={`category-title ${
                  sidebarExpanded ? "expanded" : "collapsed"
                }`}
              >
                {section.category}
              </div>
              {section.items.map((item, itemIndex) => {
                const currentPlan = getUserPlan();
                const isBlockedByPlan = false;
                const isShopBlocked =
                  item.path === "/marketplace/shop" &&
                  ![2, 5, 9, 10, 11].includes(currentPlan);
                const isItemBlocked = isBlockedByPlan || isShopBlocked;

                const menuItem = (
                  <div
                    key={itemIndex}
                    className={`menu-item ${
                      sidebarExpanded ? "expanded" : "collapsed"
                    } ${currentPath === item.path ? "active" : ""} ${
                      isItemBlocked ? "plan-blocked" : ""
                    }`}
                    onClick={() =>
                      isItemBlocked
                        ? null
                        : handleClick(item.path, item?.external)
                    }
                    title={isItemBlocked ? "Faça um upgrade para liberar" : ""}
                    style={{
                      opacity: isItemBlocked ? 0.5 : 1,
                      cursor: isItemBlocked ? "not-allowed" : "pointer",
                      color: isItemBlocked ? "#999" : "",
                    }}
                  >
                    <i className={item.icon} />
                    <span>{item.label}</span>
                    {item.badge && (
                      <span className={`menu-beta-badge menu-badge--${item.badge.toLowerCase()}`}>{item.badge}</span>
                    )}
                    {isItemBlocked && (
                      <i
                        className="las la-lock"
                        style={{ marginLeft: "auto", fontSize: "14px" }}
                      />
                    )}
                  </div>
                );

                if (isItemBlocked) {
                  return (
                    <OverlayTrigger
                      key={itemIndex}
                      placement="right"
                      overlay={
                        <Tooltip id={`tooltip-${itemIndex}`}>
                          Faça um upgrade para liberar
                        </Tooltip>
                      }
                      trigger={["hover", "focus"]}
                    >
                      <span style={{ display: "block" }}>{menuItem}</span>
                    </OverlayTrigger>
                  );
                }

                return menuItem;
              })}
            </div>
          ))}
      </>
    );
  };

  // Atualiza a classe do main-container
  React.useEffect(() => {
    const mainContainer = document.querySelector(".main-container");
    if (mainContainer) {
      mainContainer.classList.toggle("sidebar-expanded", sidebarExpanded);
      mainContainer.classList.toggle("sidebar-collapsed", !sidebarExpanded);
    }
  }, [sidebarExpanded]);

  return (
    <>
      {/* Banner de sessão ativa do cliente */}

      <div style={{ display: "flex" }}>
        <div
          className={`sidebar sidebar-menu ${sidebarExpanded ? "expanded" : "collapsed"}`}
        >
          <div
            className={`logo-container ${
              sidebarExpanded ? "expanded" : "collapsed"
            }`}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <WhitelabelLogo
                type="mobile"
                className={`logo-symbol ${
                  sidebarExpanded ? "expanded" : "collapsed"
                }`}
                fallback={
                  <img
                    src={logo_symbol}
                    alt="logo symbol"
                    className={`logo-symbol ${
                      sidebarExpanded ? "expanded" : "collapsed"
                    }`}
                  />
                }
              />
              <WhitelabelLogo
                type="main"
                className={`logo-full ${
                  sidebarExpanded ? "expanded" : "collapsed"
                }`}
                fallback={
                  <img
                    src={logo}
                    alt="logo"
                    className={`logo-full ${
                      sidebarExpanded ? "expanded" : "collapsed"
                    }`}
                  />
                }
              />
            </div>
            <button
              className={`sidebar-toggle-btn ${
                sidebarExpanded ? "expanded" : "collapsed"
              }`}
              onClick={toggleSidebar}
              title={sidebarExpanded ? "Recolher menu" : "Expandir menu"}
            >
              <i className="las la-chevron-left" />
            </button>
          </div>

          {/* Select de Conta - Habilitado para planos de agência */}
          {getUserPlan() > 1 && (
            <div
              className={`profile-select-container ${
                sidebarExpanded ? "expanded" : "collapsed"
              }`}
            >
              <label className="profile-select-label d-flex justify-content-between">
                Meu plano
                {planCategory === "founder" && (
                  <span
                    className="founder-level-badge pr-3"
                    style={{ paddingRight: 10 }}
                  >
                    Lvl. 10 🏆
                  </span>
                )}
              </label>
              <div className={`plan-display-container plan-${planCategory}`}>
                <i
                  className={`plan-icon ${
                    planCategory === "founder"
                      ? "las la-crown"
                      : planCategory === "scale"
                        ? "las la-rocket"
                        : planCategory === "growth"
                          ? "las la-chart-line"
                          : planCategory === "starter"
                            ? "las la-play"
                            : planCategory === "free"
                              ? "las la-gift"
                              : planCategory === "personal-starter"
                                ? "las la-star"
                                : planCategory === "personal-business"
                                  ? "las la-briefcase"
                                  : planCategory === "white-label"
                                    ? "las la-tags"
                                    : planCategory === "wl-plus"
                                      ? "las la-rocket"
                                      : planCategory === "wl-pro"
                                        ? "las la-crown"
                                        : "las la-user"
                  }`}
                ></i>
                <span className="plan-text">
                  {getPlanLabel(getUserPlan())}
                </span>
                {getUserPlan() === 2 && (
                  <img
                    src="/imgs/selo-fundador.png"
                    alt="Selo Fundador"
                    className="founder-crown"
                  />
                )}
              </div>
            </div>
          )}
          {/* Menu Items */}
          {renderMenuItems()}
        </div>

        <div
          className={`header-container ${
            sidebarExpanded ? "sidebar-expanded" : "sidebar-collapsed"
          }`}
        >
          {activeClientSession && (
            <div className="client-session-info d-flex align-items-center me-auto">
              <div className="session-badge d-flex align-items-center bg-info text-white px-3 py-2 rounded">
                <IconUserCheck size={16} className="me-2" />
                <div className="session-details">
                  <div
                    className="session-client fw-bold"
                    style={{ fontSize: "13px" }}
                  >
                    {activeClientSession.clientAccount?.company_name}
                  </div>
                  <div
                    className={`session-timer d-flex align-items-center ${
                      activeClientSession.isExpiringSoon ? "expiring" : ""
                    }`}
                    style={{ fontSize: "11px", opacity: 0.9 }}
                  >
                    <IconClock size={12} className="me-1" />
                    {activeClientSession.timeLeftMinutes}min restantes
                    {activeClientSession.isExpiringSoon && (
                      <span className="ms-1">⚠️</span>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={handleReturnToOriginalAccount}
                  disabled={returningToOriginal}
                  className="ms-3"
                  style={{
                    fontSize: "11px",
                    padding: "4px 8px",
                    borderColor: "rgba(255,255,255,0.5)",
                  }}
                >
                  <IconLogout size={12} />
                </Button>
              </div>
            </div>
          )}
          {/* <ThemeToggle className="me-3" size={20} /> */}
          {!isSubaccount() && (
            <button className="notification-btn">
              <IconBellFilled
                size={20}
                className="text-white"
                stroke={2}
                data-sleek
              />
            </button>
          )}

          <Dropdown className="nav-item dropdown header-profile" as="li">
            <Dropdown.Toggle
              as="a"
              variant=""
              className="profile-dropdown-toggle"
            >
              <img
                src={
                  user?.user_photo
                    ? !user?.user_photo.includes("https://")
                      ? `https://mercadoagentes.s3.amazonaws.com/${user.user_photo}`
                      : user?.user_photo
                    : require("../../../images/user.png")
                }
                alt="profile"
                className="profile-avatar"
              />
              <div className="profile-info">
                <span className="profile-name">{user?.first_name || ""}</span>
                <p className="profile-role">
                  {superuser[user?.superuser_level] || ""}
                </p>
              </div>
            </Dropdown.Toggle>

            <Dropdown.Menu align="right" className="mt-2">
              <Link
                to={`/user-details/${user?.user_id || ""}`}
                className="dropdown-item ai-icon"
              >
                <svg
                  id="icon-user1"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-primary"
                  width={18}
                  height={18}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx={12} cy={7} r={4} />
                </svg>
                <span className="ml-2">Conta</span>
              </Link>

              {!isSubaccount() && (
                <button
                  onClick={() => setShowTermsModal(true)}
                  className="dropdown-item ai-icon"
                  style={{
                    background: "none",
                    border: "none",
                    textAlign: "left",
                    width: "100%",
                    padding: "0.375rem 1rem",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="text-info"
                    width={18}
                    height={18}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                  <span className="ml-2">Termos de Uso</span>
                </button>
              )}

              <button
                onClick={Logout}
                className="dropdown-item ai-icon"
                style={{
                  background: "none",
                  border: "none",
                  textAlign: "left",
                  width: "100%",
                  padding: "0.375rem 1rem",
                }}
              >
                <svg
                  id="icon-logout"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-danger"
                  width={18}
                  height={18}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1={21} y1={12} x2={9} y2={12} />
                </svg>
                <span className="ml-2">Sair</span>
              </button>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {/* Modal de Termos de Uso */}
      <Modal
        show={showTermsModal}
        onHide={() => setShowTermsModal(false)}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Termos de Uso</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <NewTermsOfUse setShowModal={setShowTermsModal} />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Header;
